import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useState} from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import {withLoadingScreen} from 'util/Promises';

import ProcedureRunDesignFlowchartEditor from './ProcedureRunDesignFlowchartEditor';
import ProcedureSearchSidepanel, {
  PROCEDURE_SELECTION_DND_MIME_TYPE,
  SelectedProcedure,
} from './ProcedureSearchSidepanel';

/**
 * Renders the editor for a procedure run plan that has no linked flowchart version yet
 * (procedure-less). Reuses the real flowchart editor shell with empty methods/conditions so the
 * empty state matches the populated editor (same sidebar, panels, and canvas background). Dragging
 * a procedure card from the search sidepanel onto the empty canvas attaches its flowchart version
 * to the existing plan and refetches.
 *
 */
export default function EmptyProcedureRunPlanDesignTabContents({
  procedureRunPlanId,
  studyId,
  studyType,
  procedureType,
  refetch,
}: {
  procedureRunPlanId: string;
  studyId: string;
  studyType: StudyType;
  procedureType: ProcedureType;
  refetch: () => void;
}): JSX.Element {
  const {classes} = useStyles();
  const [isDropTargetActive, setIsDropTargetActive] = useState(false);
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });

  function parseDroppedProcedure(event: React.DragEvent<HTMLDivElement>): SelectedProcedure | null {
    const dndPayload = event.dataTransfer.getData(PROCEDURE_SELECTION_DND_MIME_TYPE);
    if (dndPayload.length === 0) {
      return null;
    }
    let parsed: Partial<SelectedProcedure>;
    try {
      parsed = JSON.parse(dndPayload) as Partial<SelectedProcedure>;
    } catch (err) {
      // Return null if the user tries to drop something without a valid JSON payload
      if (err instanceof SyntaxError) {
        return null;
      }
      throw err;
    }
    // Return null if the user tries to drop something other than the procedure card
    if (
      typeof parsed.procedureId !== 'string' ||
      typeof parsed.workflowFlowchartConfigVersionId !== 'string'
    ) {
      return null;
    }
    return {
      procedureId: parsed.procedureId,
      workflowFlowchartConfigVersionId: parsed.workflowFlowchartConfigVersionId,
    };
  }

  async function handleSelectProcedure({workflowFlowchartConfigVersionId}: SelectedProcedure): Promise<void> {
    await withLoadingScreen(
      updateProcedureRunPlan({
        id: procedureRunPlanId,
        studyId,
        workflowFlowchartConfigVersionId,
      }),
      `Adding ${procedureString} to plan...`
    );
    refetch();
  }

  async function handleDropSelection(selectedProcedure: SelectedProcedure): Promise<void> {
    await handleSelectProcedure(selectedProcedure);
  }

  return (
    <div
      className={classes.designBackground}
      {...getTestProps({component: 'EmptyProcedureRunPlanDesignTabContents', element: 'root'})}
    >
      <div>
        <ProcedureRunDesignFlowchartEditor
          conditions={[]}
          methods={[]}
          activeMethodId={null}
          procedureType={procedureType}
          selectedMethodId={null}
          onChangeSelectedMethod={noop}
          // Force the left panel open so the procedure search sidepanel is visible immediately.
          isInEditState={true}
          allowEditingPersistedConditions={true}
          edgeSpecs={[]}
          leftPanelChildren={<ProcedureSearchSidepanel procedureType={procedureType} studyType={studyType} />}
          rightPanelChildren={null}
          canvasDnd={{
            isDropTargetActive,
            onDragEnter: (event) => {
              event.preventDefault();
              if (!event.dataTransfer.types.includes(PROCEDURE_SELECTION_DND_MIME_TYPE)) {
                return;
              }
              setIsDropTargetActive(true);
            },
            onDragOver: (event) => {
              event.preventDefault();
              if (event.dataTransfer.types.includes(PROCEDURE_SELECTION_DND_MIME_TYPE)) {
                event.dataTransfer.dropEffect = 'copy';
              }
            },
            onDragLeave: (event) => {
              event.preventDefault();
              setIsDropTargetActive(false);
            },
            onDrop: async (event) => {
              event.preventDefault();
              setIsDropTargetActive(false);
              const selectedProcedure = parseDroppedProcedure(event);
              if (selectedProcedure == null) {
                return;
              }
              await handleDropSelection(selectedProcedure);
            },
          }}
        />
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  designBackground: {
    backgroundColor: theme.colors.white,
  },
}));
